import { useEffect, useRef, useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faComment } from '@fortawesome/pro-light-svg-icons'

import {
  AddCommentBox,
  Box,
  Conversation as ConversationBase,
  DashboardModuleListItem,
  SectionDivider,
} from '@domain-group/suit-theme'

import useArtwork from '@hooks/useArtwork'
import useArtworkReviews from '@hooks/useArtworkReviews'
import useConversationContainer from '@hooks/useConversationContainer'
import useConversations from '@hooks/useConversations'
import useStatuses from '@hooks/useStatuses'

import handleCallbackAction from '@functions/handleCallbackAction'

import BlankState from '@components/EntityActivity/BlankState'
import Comment from '@components/Comment'
import CreateArtworkReviewButton from '@components/CreateArtworkReviewButton'

import type { ArtworkModel } from '@models/types'

import BrandedArtworkComment from './BrandedArtworkComment'
import ConversationImageUrl from './assets/conversation.svg'

const defaultState = {
  showConversation: false,
}

interface BrandedArtworkConversationProps {
  accentColor?: string,
  artwork: Partial<ArtworkModel>,
  fontColor?: string,
}

const BrandedArtworkConversation = (props: BrandedArtworkConversationProps) => {
  const {
    accentColor, artwork, fontColor,
  } = props

  const [state, setState] = useState(defaultState)
  const { showConversation } = state

  const conversationPayload = useConversations({
    filters: { subject_id: artwork?.id, subject_type: 'Artwork' },
    performHttpRequests: !!artwork?.id,
    requestOptions: {
      include_conversation_comments: true,
    },
  })
  const { filteredConversations } = conversationPayload
  const artworkConversation = filteredConversations[0]

  const conversationContainerPayload = useConversationContainer({
    conversation: {
      id: artworkConversation?.id,
      // @ts-expect-error TS2322
      subject_id: artwork?.id,
      subject_type: 'Artwork',
    },
    performHttpRequests: !!artwork?.id,
    requestOptions: {
      include_artwork_artwork_reviews: true,
      include_artwork_public_download_link: true,
      include_attachment_file: true,
      include_comment_attachments: true,
      include_conversation_comments: true,
    },
  })
  const {
    callbacks: {
      createCommentWithConversation,
    },
    creating,
    filteredComments,
    filteredCommentsCount,
    hasFilteredComments,
    latestCommentWithAttachment,
    loading,
  } = conversationContainerPayload

  // slice() to make a copy & prevent altering the display order of comments
  const sortedFilteredComments = filteredComments.slice().sort((a, b) => b.id - a.id)
  const latestComment = sortedFilteredComments[0]

  const containerRef = useRef(null)

  const { isApproved: artworkIsApproved } = useArtwork(artwork)

  const { filteredArtworkReviews } = useArtworkReviews({
    filters: {
      artwork_id: artwork?.id,
    },
  })
  const { filteredStatuses } = useStatuses({ filters: { status_keys: ['approved'] } })
  const approvedStatus = filteredStatuses[0]

  const artworkReviewIsApproved = filteredArtworkReviews?.some(review => review?.status_id === approvedStatus?.id)

  const isApproved = artworkIsApproved || artworkReviewIsApproved

  useEffect(() => {
    if (containerRef.current){
      // Scroll to bottom of container to show latest comment
      // @ts-expect-error TS2339
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [containerRef.current, filteredCommentsCount])

  const hasComments = !loading && hasFilteredComments

  return (
    <>
      <Box flexDirection="column" paddingX="large" paddingBottom={hasComments ? 'none' : 'large'}>
        {!isApproved && (
          <AddCommentBox
            buttonText="Request Changes"
            buttonProps={{
              buttonStyle: 'secondaryUtility',
              justifySelf: 'end',
            }}
            callbacks={{
              addComment: comment => handleCallbackAction({
                callbacks: {
                  action: createCommentWithConversation,
                },
                entityParams: comment,
                toastText: 'Changes Requested',
                requestOptions: {
                  include_conversation_comments: true,
                },
              }),
            }}
            showPrivacyToggle={false}
          >
            <CreateArtworkReviewButton
              // @ts-expect-error TS2322
              artwork={artwork}
              boxProps={{ marginLeft: 'medium', order: 2 }}
              buttonStyle={{
                backgroundColor: accentColor,
                borderColor: accentColor,
                color: fontColor,
              }}
              buttonVariant="primaryEdit"
            />
          </AddCommentBox>
        )}

        {hasComments && (
          <Box flexDirection="column" marginTop="large">
            <SectionDivider>CHANGES REQUESTED</SectionDivider>

            <Comment
              comment={latestComment}
              isLastComment
            />
          </Box>
        )}
      </Box>

      <DashboardModuleListItem
        boxProps={{
          borderTop: '1px solid',
          borderBottom: 'none',
          borderColor: 'lineColor',
        }}
        imageComponent={(
          <Box marginRight="medium" width="16px">
            <FontAwesomeIcon icon={faComment} />
          </Box>
        )}
        onClick={() => setState({ showConversation: !showConversation })}
        title="Conversations"
      />

      {showConversation && (
        // @ts-expect-error TS2741
        <ConversationBase
          addCommentBoxProps={{ showPrivacyToggle: false }}
          boxProps={{
            marginTop: 0,
            padding: 'large',
          }}
          commentsBoxProps={{
            alignItems: !hasComments ? 'center' : undefined,
            height: !hasComments ? 270 : undefined,
            justifyContent: !hasComments ? 'center' : undefined,
            maxHeight: '420px',
          }}
          commentCount={filteredCommentsCount}
          creating={creating}
          loading={loading}
          requestOptions={{ include_conversation_comments: true }}
          showAddCommentBox={false}
        >
          {!hasComments && (
            <BlankState
              imageUrl={ConversationImageUrl}
              title="No Comments"
              subtitle="Keep a detailed history of work, decisions and requests in the conversation."
            />
          )}

          {filteredComments.map((comment, index) => (
            <BrandedArtworkComment
              comment={comment}
              // @ts-expect-error TS2339
              hasLatestAttachment={latestCommentWithAttachment?.id === comment.id}
              isFirstComment={index === 0}
              isLastComment={index === (filteredCommentsCount - 1)}
              key={comment.id}
            />
          ))}
        </ConversationBase>
      )}
    </>
  )
}

export default BrandedArtworkConversation
